// What the outside widgets need to know (the weather's place and units, the
// house widget's Home Assistant entities), synced on the Settings record as
// `widgets` and repaired on the way in, like the phone's layout. Before Home
// had them these lived on the desk, in this device's localStorage under
// `desk`; a device that chose them there keeps them until something is set in
// Settings → Home screen.

export interface WidgetPlace {
  name: string
  latitude: number
  longitude: number
}

export type WeatherUnits = 'celsius' | 'fahrenheit'

export interface WidgetConfig {
  place: WidgetPlace | null
  units: WeatherUnits
  /** Home Assistant entity ids, in the order they were chosen */
  house: string[]
}

export const MAX_HOUSE = 60

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
const num = (v: unknown, lim: number): v is number =>
  typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= lim

const placeOf = (v: unknown): WidgetPlace | null =>
  isObj(v) && num(v.latitude, 90) && num(v.longitude, 180)
    ? {
        name: typeof v.name === 'string' ? v.name.slice(0, 120) : '',
        latitude: v.latitude,
        longitude: v.longitude,
      }
    : null

const entitiesOf = (v: unknown): string[] | null =>
  Array.isArray(v)
    ? [
        ...new Set(v.filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length <= 120)),
      ].slice(0, MAX_HOUSE)
    : null

/**
 * The widgets' config from the synced value, falling back field by field to
 * what the old desk kept on this device (its keys were `location`, `units` and
 * `homeEntities`). Anything unusable is left unset.
 */
export const widgetConfig = (synced: unknown, legacy?: unknown): WidgetConfig => {
  const s = isObj(synced) ? synced : {}
  const l = isObj(legacy) ? legacy : {}
  const units = s.units ?? l.units
  return {
    place: placeOf(s.place) ?? placeOf(l.location),
    units: units === 'fahrenheit' ? 'fahrenheit' : 'celsius',
    house: entitiesOf(s.house) ?? entitiesOf(l.homeEntities) ?? [],
  }
}

/** What to store: the whole config, so the desk's old values are carried over the first time anything is set. */
export const widgetPatch = (c: WidgetConfig, change: Partial<WidgetConfig>) => {
  const next = { ...c, ...change }
  return {
    ...(next.place ? { place: next.place } : {}),
    units: next.units,
    house: next.house.slice(0, MAX_HOUSE),
  }
}

/** Choose or drop one entity. */
export const toggleEntity = (house: string[], id: string): string[] =>
  house.includes(id) ? house.filter((x) => x !== id) : [...house, id].slice(0, MAX_HOUSE)

/** The desk's old per-device settings, read once; nothing is written back under this key but the voice switches. */
export const legacyDesk = (): unknown => {
  try {
    const raw = localStorage.getItem('desk')
    return raw ? (JSON.parse(raw) as unknown) : undefined
  } catch {
    return undefined
  }
}
